/**
 * Avatar option sets + the rendered avatar glyph used by the Settings
 * profile card and the picker dialog. 16 lucide icons × 8 token colors;
 * the ids are persisted on the profile (`iconId` / `iconColor`) so they
 * must stay stable. Color values are semantic viz/brand tokens only.
 */
import type { LucideIcon } from 'lucide-react'
import {
  Bug,
  ChartLine,
  Cloud,
  Compass,
  Eye,
  Fingerprint,
  Globe,
  KeyRound,
  Lock,
  Radar,
  Satellite,
  ScanSearch,
  Server,
  Shield,
  Terminal,
  Zap,
} from 'lucide-react'

export interface AvatarIconOption {
  id: string
  label: string
  icon: LucideIcon
}

export interface AvatarColorOption {
  id: string
  label: string
  /** CSS custom-property reference, e.g. `var(--viz-3)`. */
  token: string
}

export const AVATAR_ICONS: readonly AvatarIconOption[] = [
  { id: 'shield', label: 'Shield', icon: Shield },
  { id: 'key', label: 'Key', icon: KeyRound },
  { id: 'lock', label: 'Lock', icon: Lock },
  { id: 'fingerprint', label: 'Fingerprint', icon: Fingerprint },
  { id: 'radar', label: 'Radar', icon: Radar },
  { id: 'scan', label: 'Scan', icon: ScanSearch },
  { id: 'graph', label: 'Graph', icon: ChartLine },
  { id: 'eye', label: 'Eye', icon: Eye },
  { id: 'globe', label: 'Globe', icon: Globe },
  { id: 'compass', label: 'Compass', icon: Compass },
  { id: 'satellite', label: 'Satellite', icon: Satellite },
  { id: 'cloud', label: 'Cloud', icon: Cloud },
  { id: 'server', label: 'Server', icon: Server },
  { id: 'terminal', label: 'Terminal', icon: Terminal },
  { id: 'bolt', label: 'Bolt', icon: Zap },
  { id: 'bug', label: 'Bug', icon: Bug },
]

export const AVATAR_COLORS: readonly AvatarColorOption[] = [
  { id: 'sienna', label: 'Sienna', token: 'var(--brand-500)' },
  { id: 'malibu', label: 'Malibu', token: 'var(--accent-500)' },
  { id: 'moss', label: 'Moss', token: 'var(--viz-3)' },
  { id: 'amber', label: 'Amber', token: 'var(--viz-4)' },
  { id: 'violet', label: 'Violet', token: 'var(--viz-5)' },
  { id: 'rose', label: 'Rose', token: 'var(--viz-6)' },
  { id: 'teal', label: 'Teal', token: 'var(--viz-7)' },
  { id: 'slate', label: 'Slate', token: 'var(--viz-8)' },
]

/** Unknown ids (e.g. a future fixture change) fall back to the first option. */
export function avatarIcon(iconId: string): AvatarIconOption {
  return AVATAR_ICONS.find((o) => o.id === iconId) ?? AVATAR_ICONS[0]
}

export function avatarColor(colorId: string): AvatarColorOption {
  return AVATAR_COLORS.find((o) => o.id === colorId) ?? AVATAR_COLORS[0]
}

export interface ProfileAvatarProps {
  iconId: string
  iconColor: string
  /** Outer diameter in px. @default 40 */
  size?: number
}

export function ProfileAvatar({ iconId, iconColor, size = 40 }: ProfileAvatarProps) {
  const icon = avatarIcon(iconId)
  const color = avatarColor(iconColor)
  const Glyph = icon.icon
  return (
    <span
      role="img"
      aria-label={`Avatar: ${icon.label.toLowerCase()} glyph, ${color.label.toLowerCase()} tint`}
      style={{
        width: size,
        height: size,
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--r-pill)',
        background: `color-mix(in oklab, ${color.token} 16%, var(--bg-elev))`,
        border: `1px solid color-mix(in oklab, ${color.token} 40%, var(--border))`,
        color: color.token,
      }}
    >
      <Glyph size={Math.round(size * 0.5)} strokeWidth={1.75} aria-hidden="true" />
    </span>
  )
}
